import React, { useEffect, useMemo, useState } from "react";

import { Label, Modal, Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import { Button, ButtonWrapper } from "ds/Button";
import * as Input from "ds/Input";
import Select from "ds/Select";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { gitProjectSelector } from "Reducers/project/project";
import { domainsSelector } from "Reducers/project/settings/domains/domains.selectors";

import * as S from "./DeleteModal.styles";

import type { EnvironmentDomain, Domain, Project } from "@packages/client";

type DeleteProductionDomain = (
  domain: Domain,
  newDefaultDomain?: Domain
) => void;
type DeleteEnvironmentDomain = (environmentDomain: EnvironmentDomain) => void;

type DeleteModalPropType = {
  closeModal: () => void;
  deleteProductionDomain?: DeleteProductionDomain;
  deleteEnvironmentDomain?: DeleteEnvironmentDomain;
  domain?: Domain;
  isOpen: boolean;
  project?: Project;
  showingWarning?: boolean;
  capabilityCustomDomains?: boolean;
};

const DeleteModal = ({
  closeModal,
  deleteProductionDomain,
  deleteEnvironmentDomain,
  domain,
  isOpen,
  showingWarning,
  capabilityCustomDomains
}: DeleteModalPropType) => {
  const intl = useIntl();
  const project = useSelectorWithUrlParams(gitProjectSelector);

  const [defaultDomain, setDefaultDomain] = useState<Domain | undefined>();
  const domains = useSelectorWithUrlParams(domainsSelector);

  const domainList = useMemo(() => {
    return domains
      .filter(elt => elt.id !== domain?.id)
      .map(elt => {
        return { label: elt.name, value: elt.id, domain: elt };
      });
  }, [domain?.id, domains]);

  useEffect(() => {
    if (domainList?.length)
      setDefaultDomain(
        domainList?.find(d => d.value === project?.default_domain)?.domain ??
          domainList[0]?.domain
      );
  }, [domainList, project?.default_domain]);

  const handleChange = selected => {
    setDefaultDomain(selected.domain);
  };

  const handleConfirmDefaultDomain = (e, domain?: Domain) => {
    e.preventDefault();
    let newDefault;
    if (project?.default_domain === domain?.name && domains.length > 1) {
      newDefault = defaultDomain;
    }

    if (deleteProductionDomain && domain) {
      deleteProductionDomain(domain, newDefault);
    }

    closeModal();
  };

  const handleConfirmDomain = (e, domain?: EnvironmentDomain) => {
    e.preventDefault();

    if (deleteEnvironmentDomain && domain) {
      deleteEnvironmentDomain(domain);
    }

    closeModal();
  };

  const isShowingWarning = showingWarning || capabilityCustomDomains;

  return (
    <Modal isOpen={isOpen} onOpenChange={closeModal} handleClose={closeModal}>
      <Typography.Title tag="h3">
        {intl.formatMessage({
          id: "settings.domains.modal.delete.title"
        })}
      </Typography.Title>

      <>
        <Typography.BodyText>
          {intl.formatMessage<React.ReactNode>(
            {
              id: `settings.domains.modal.delete.body${
                project?.default_domain === domain?.name
                  ? `.default.${domains.length === 2 ? "switch" : "select"}`
                  : ""
              }`
            },
            {
              b: chunks => <b>{chunks}</b>,
              domain: domain?.id,
              otherDomain: domains.find(elt => elt.id !== domain?.id)?.id
            }
          )}
        </Typography.BodyText>

        {project?.default_domain === domain?.name && domains.length > 2 && (
          <div style={{ marginTop: "32px" }}>
            <Input.Root>
              <Label htmlFor="domain" isValid required>
                {intl.formatMessage({
                  id: "settings.domains.modal.delete.select"
                })}
              </Label>
              <Select
                id="domain"
                isClearable={false}
                isMulti={false}
                isSearchable
                onChange={handleChange}
                options={domainList}
                value={
                  defaultDomain
                    ? {
                        label: defaultDomain?.name,
                        value: defaultDomain?.id,
                        domain: defaultDomain
                      }
                    : domainList[0]
                }
              />
            </Input.Root>
          </div>
        )}
      </>

      {isShowingWarning && (
        <S.WarningContainer>
          <S.Warning width={60} />

          <S.CustomDomainsText>
            {intl.formatMessage({
              id: `settings.domains.modal.delete.body.custom_domains`
            })}
          </S.CustomDomainsText>
        </S.WarningContainer>
      )}

      <ButtonWrapper spacing="modal" justifyContent="end">
        <Button variant="secondary" analyticId="cancel" onClick={closeModal}>
          {capitalize(intl.formatMessage({ id: "cancel" }))}
        </Button>
        <Button
          type="submit"
          analyticId="settings.domains.modal.delete.confirm"
          onClick={e =>
            deleteProductionDomain
              ? handleConfirmDefaultDomain(e, domain)
              : handleConfirmDomain(e, domain as EnvironmentDomain)
          }
          data-testid="confirm-btn"
        >
          {capitalize(
            intl.formatMessage({
              id: "settings.domains.modal.delete.confirm"
            })
          )}
        </Button>
      </ButtonWrapper>
    </Modal>
  );
};

export default DeleteModal;
